import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link, useLocation } from "react-router-dom";
import DeletePopUp from "../DeletePopUp";
import { IComplaint } from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { fetchComplaints } from "../../api/ComplaintApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { usePermission } from "../../hooks/usePermission";
import { darkenColor, formatDateBySetting } from "../../exports/functions";
import TableFooter from "../TableFooter";
import { useSettings } from "../../context/SettingsContext";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const ComplaintTable = ({
  viewAll,
  complaint,
  setComplaint,
}: {
  viewAll?: boolean;
  complaint: IComplaint[];
  setComplaint: Dispatch<SetStateAction<IComplaint[]>>;
}) => {
  const { setLoading } = useLoader();
  const { statusOptions, categories } = useOption();
  const { canEdit, canDelete } = usePermission();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const { generalSettings } = useSettings();
  const isMobile = useMobile();
  const location = useLocation();
  const [selectedId, setSelectedId] = useState<number | string | null>(
    location.state?.selectedId ?? null,
  );

  useEffect(() => {
    if (location.state?.selectedId) {
      setSelectedId(location.state.selectedId);
      window.history.replaceState({ ...window.history.state, usr: null }, "");
    }
  }, [location.state?.selectedId]);

  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  const filteredComplaints = (complaint || []).filter((item) =>
    selectedId
      ? item.id === selectedId || item.id === Number(selectedId)
      : true,
  );

  const itemsPerPage = showAll
    ? Math.max(filteredComplaints.length, 1)
    : viewAll
      ? 25
      : 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredComplaints.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const totalPages = Math.ceil(filteredComplaints.length / itemsPerPage);

  const handleClose = () => {
    setShowDelete(false);
  };

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Complaint Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const priorityStyle = (priority: string) => {
    const priorityOptions =
      categories?.find((item) => item.name === "Complaint Priority")
        ?.dropdowns || [];

    const matchedPriority = priorityOptions.find(
      (item) => item.label?.toLowerCase() === priority?.toLowerCase(),
    );

    return {
      backgroundColor: matchedPriority?.color || "#ccc",
      color: darkenColor(matchedPriority?.color || "#ccc", 40),
    };
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/complaints/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchComplaints()
          .then((complaintData) => {
            setComplaint(complaintData);
            setShowDelete(false);
            setDeleteId(null);
            sendNotification(
              `Complaint deleted`,
              user?.user?.userName || "User",
            );
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Complaint deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
            setTimeout(() => {
              setLoading(false);
            }, 1000);
          });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="table-dashboard-card">
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th className="table-xs">S. no.</th>
                <th className="table-md">Complainant Name</th>
                <th className="table-md">Cooperative</th>
                <th className="table-sm">Category</th>
                <th className="table-sm">Filed on</th>
                <th className="table-sm">Priority</th>
                <th className="table-md">Status</th>
                <th className="table-sm">Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentData.length > 0 ? (
                currentData.map((item, index) => {
                  const canEditComplaint =
                    canEdit("complaints") &&
                    item.status?.toLowerCase() !== "solved";

                  return (
                    <tr key={index}>
                      <td
                        className="table-xs text-danger"
                        title={String(indexOfFirstItem + index + 1)}
                      >
                        {String(indexOfFirstItem + index + 1).padStart(2, "0")}
                      </td>
                      <td
                        className="table-md table-bold"
                        title={item.complainantName || "-"}
                      >
                        {item.complainantName || "-"}
                      </td>
                      <td
                        className="table-md table-bold"
                        title={item.cooperativeName || "-"}
                      >
                        {item.cooperativeName || "-"}
                      </td>
                      <td
                        className="table-sm"
                        title={item.complaintType || "-"}
                      >
                        {item.complaintType || "-"}
                      </td>
                      <td
                        className="table-sm"
                        title={
                          formatDateBySetting(
                            item.filedOn,
                            generalSettings?.dateFormat,
                          ) || item.filedOn
                        }
                      >
                        {formatDateBySetting(
                          item.filedOn,
                          generalSettings?.dateFormat,
                        ) ||
                          item.filedOn ||
                          "-"}
                      </td>
                      <td className="table-sm">
                        <span
                          className="badge rounded-pill px-3 py-2 mw-100"
                          style={priorityStyle(item.complaintPriority)}
                          title={item.complaintPriority || "-"}
                        >
                          {item.complaintPriority || "-"}
                        </span>
                      </td>

                      <td className="table-md">
                        <span
                          className="badge rounded-pill px-3 py-2 mw-100"
                          style={statusStyle(item.status || "")}
                          title={item.status}
                        >
                          {item.status}
                        </span>
                      </td>

                      <td className="table-sm">
                        <div className="d-flex gap-3 align-items-center">
                          <Link to={`/complaints/view/${item.id}`}>
                            <i
                              className="cursor-pointer"
                              data-toggle="tooltip"
                              data-placement="top"
                              title="View"
                            >
                              <Eye />
                            </i>
                          </Link>
                          <Link
                            to={
                              canEditComplaint
                                ? `/complaints/edit/${item.id}`
                                : "#"
                            }
                            onClick={(e) => {
                              if (!canEditComplaint) {
                                e.preventDefault();
                              }
                            }}
                            style={{
                              pointerEvents: canEditComplaint ? "auto" : "none",
                              opacity: canEditComplaint ? 1 : 0.5,
                            }}
                          >
                            <i
                              data-toggle="tooltip"
                              data-placement="top"
                              title="Edit"
                            >
                              <Pencil size={20} />
                            </i>
                          </Link>
                          <i
                            className={`fa-solid fa-trash text-danger ${
                              canDelete("complaints")
                                ? "cursor-pointer"
                                : "opacity-50"
                            }`}
                            data-toggle="tooltip"
                            data-placement="top"
                            title={
                              canDelete("complaints")
                                ? "Delete"
                                : "No permission"
                            }
                            style={{
                              pointerEvents: canDelete("complaints")
                                ? "auto"
                                : "none",
                            }}
                            onClick={() => {
                              if (!canDelete("complaints")) return;

                              setDeleteId(item?.id || 0);
                              setShowDelete(true);
                            }}
                          ></i>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-muted">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TableFooter
          isMobile={isMobile}
          totalItems={filteredComplaints.length}
          label="Complaints"
          itemsPerPage={viewAll ? 25 : 5}
          currentPage={currentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          showAll={showAll}
          onToggleShowAll={() => {
            setShowAll((prev) => !prev);
            setCurrentPage(1);
          }}
          onPageChange={(page) => setCurrentPage(page)}
          className="table-footer"
        />
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default ComplaintTable;
